import type { ContrastLevel } from "@/color/contrast";
import type { Hex } from "@/color/oklch";
import type { PaletteKey } from "./palette";

export type Variant = "dark" | "light";
export type GroupId = "terminal" | "syntax" | "ui" | "herdr" | "claude" | "weechat";
export type TokenId = `${GroupId}.${string}`;

/**
 * Where a token gets its color: a palette swatch (`$blue`), another token (`@ui.accent`)
 * or a literal hex. References keep "change blue once, everything follows" working.
 */
export type Ref = `$${PaletteKey}` | `@${TokenId}` | Hex;

/** Derived defaults may also blend or shade references. Overrides are always plain refs. */
export type Expr = Ref | { mix: [Expr, Expr, number] } | { shade: [Expr, number] };

export interface TokenDef {
  id: TokenId;
  group: GroupId;
  key: string;
  label: string;
  section: string;
  hint?: string;
  role: "fg" | "bg";
  default: Expr | { dark: Expr; light: Expr };
  /** Contrast check against another token (usually the background it is drawn on). */
  contrast?: { against: TokenId; level: ContrastLevel };
}

export interface GroupInfo {
  id: GroupId;
  label: string;
  hint: string;
}

export const GROUPS: GroupInfo[] = [
  { id: "terminal", label: "Terminal", hint: "Ghostty colors and the ANSI 16 palette" },
  { id: "syntax", label: "Syntax", hint: "Code highlighting (Neovim, Claude Code diffs)" },
  { id: "ui", label: "Editor UI", hint: "Neovim interface, diagnostics and diffs" },
  { id: "herdr", label: "herdr", hint: "herdr [theme.custom] tokens" },
  { id: "claude", label: "Claude Code", hint: "Claude Code custom theme overrides" },
  { id: "weechat", label: "WeeChat", hint: "WeeChat color options (256-color)" },
];

const mix = (a: Expr, b: Expr, t: number): Expr => ({ mix: [a, b, t] });
const shade = (a: Expr, dl: number): Expr => ({ shade: [a, dl] });
const BG = "@terminal.background" as const;
const FG = "@terminal.foreground" as const;

type Opts = Partial<Pick<TokenDef, "hint" | "contrast" | "role">>;

function defineGroup(
  group: GroupId,
  rows: [section: string, key: string, label: string, def: TokenDef["default"], opts?: Opts][],
): TokenDef[] {
  return rows.map(([section, key, label, def, opts]) => ({
    id: `${group}.${key}` as TokenId,
    group,
    key,
    label,
    section,
    role: opts?.role ?? "fg",
    default: def,
    hint: opts?.hint,
    contrast: opts?.contrast,
  }));
}

const onBg = (level: ContrastLevel = "text"): Opts => ({
  contrast: { against: "terminal.background", level },
});
const onEditor = (level: ContrastLevel = "text"): Opts => ({
  contrast: { against: "ui.editorBg", level },
});
const bg: Opts = { role: "bg" };

// ---------------------------------------------------------------- terminal

const ANSI_NAMES = [
  "black",
  "red",
  "green",
  "yellow",
  "blue",
  "magenta",
  "cyan",
  "white",
  "bright black",
  "bright red",
  "bright green",
  "bright yellow",
  "bright blue",
  "bright magenta",
  "bright cyan",
  "bright white",
];

const ANSI_DEFAULTS: { dark: Ref; light: Ref }[] = [
  { dark: "$bg3", light: "$fg1" },
  { dark: "$red", light: "$red" },
  { dark: "$green", light: "$green" },
  { dark: "$yellow", light: "$yellow" },
  { dark: "$blue", light: "$blue" },
  { dark: "$purple", light: "$purple" },
  { dark: "$cyan", light: "$cyan" },
  { dark: "$fg2", light: "$bg4" },
  { dark: "$fg3", light: "$fg3" },
  { dark: "$brightRed", light: "$brightRed" },
  { dark: "$brightGreen", light: "$brightGreen" },
  { dark: "$brightYellow", light: "$brightYellow" },
  { dark: "$brightBlue", light: "$brightBlue" },
  { dark: "$brightPurple", light: "$brightPurple" },
  { dark: "$brightCyan", light: "$brightCyan" },
  { dark: "$fg0", light: "$bg2" },
];

const TERMINAL = defineGroup("terminal", [
  ["Base", "background", "Background", "$bg1", bg],
  ["Base", "foreground", "Foreground", "$fg1", onBg()],
  ["Base", "cursor", "Cursor", "$fg1", onBg("secondary")],
  ["Base", "cursorText", "Cursor text", "$bg1", { role: "bg" }],
  ["Base", "selectionBg", "Selection background", "@ui.selection", bg],
  [
    "Base",
    "selectionFg",
    "Selection foreground",
    FG,
    { contrast: { against: "terminal.selectionBg", level: "text" } },
  ],
  ...ANSI_DEFAULTS.map(
    (d, i) =>
      [
        i < 8 ? "ANSI normal" : "ANSI bright",
        `ansi${i}`,
        `${i} · ${ANSI_NAMES[i]}`,
        d,
        i === 0 || i === 15 ? {} : onBg("secondary"),
      ] as [string, string, string, TokenDef["default"], Opts],
  ),
]);

// ---------------------------------------------------------------- syntax

export const SYNTAX_KEYS = [
  "comment",
  "keyword",
  "control",
  "function",
  "method",
  "string",
  "escape",
  "regex",
  "number",
  "constant",
  "boolean",
  "type",
  "builtin",
  "variable",
  "parameter",
  "property",
  "operator",
  "punctuation",
  "tag",
  "attribute",
  "namespace",
  "macro",
  "heading",
  "link",
] as const;
export type SyntaxKey = (typeof SYNTAX_KEYS)[number];

const SYNTAX = defineGroup("syntax", [
  ["Code", "comment", "Comment", "$fg3", onEditor("secondary")],
  ["Code", "keyword", "Keyword", "$purple", onEditor()],
  ["Code", "control", "Control flow", "$purple", { ...onEditor(), hint: "if, return, for" }],
  ["Code", "function", "Function", "$blue", onEditor()],
  ["Code", "method", "Method call", "$blue", onEditor()],
  ["Code", "string", "String", "$green", onEditor()],
  ["Code", "escape", "String escape", "$pink", onEditor()],
  ["Code", "regex", "Regex", "$cyan", onEditor()],
  ["Code", "number", "Number", "$orange", onEditor()],
  ["Code", "constant", "Constant", "$orange", onEditor()],
  ["Code", "boolean", "Boolean", "$orange", onEditor()],
  ["Code", "type", "Type", "$yellow", onEditor()],
  ["Code", "builtin", "Builtin", "$red", { ...onEditor(), hint: "self, vim, print" }],
  ["Code", "variable", "Variable", "$fg1", onEditor()],
  ["Code", "parameter", "Parameter", "$fg1", onEditor()],
  ["Code", "property", "Property / field", "$cyan", onEditor()],
  ["Code", "operator", "Operator", "$fg2", onEditor("secondary")],
  ["Code", "punctuation", "Punctuation", "$fg2", onEditor("secondary")],
  ["Code", "tag", "Tag", "$red", onEditor()],
  ["Code", "attribute", "Attribute", "$yellow", onEditor()],
  ["Code", "namespace", "Namespace / module", "$yellow", onEditor()],
  ["Code", "macro", "Macro / preprocessor", "$pink", onEditor()],
  ["Markup", "heading", "Heading", "$orange", onEditor()],
  ["Markup", "link", "Link / URL", "$cyan", onEditor()],
]);

// ---------------------------------------------------------------- editor UI

const UI = defineGroup("ui", [
  ["Editor", "editorBg", "Background", BG, bg],
  ["Editor", "editorFg", "Foreground", FG, onEditor()],
  ["Editor", "panelBg", "Panel / float background", "$bg0", bg],
  ["Editor", "cursorLine", "Cursor line", "$bg2", bg],
  ["Editor", "lineNr", "Line number", "$bg4", onEditor("decorative")],
  ["Editor", "lineNrActive", "Current line number", "$fg2", onEditor("secondary")],
  ["Editor", "selection", "Selection (Visual)", "$bg3", bg],
  ["Editor", "nonText", "Whitespace / indent guide", "$bg3", onEditor("decorative")],
  ["Editor", "border", "Border / separator", "$bg4", onEditor("decorative")],
  ["Editor", "matchParen", "Matching bracket", "$bg4", bg],
  [
    "Editor",
    "searchBg",
    "Search match",
    { dark: mix(BG, "$yellow", 0.35), light: mix(BG, "$yellow", 0.4) },
    bg,
  ],
  ["Editor", "curSearchBg", "Current search match", "$orange", bg],
  ["Editor", "accent", "Accent", "$blue", onEditor()],
  ["Chrome", "statusBg", "Status line background", "$bg2", bg],
  [
    "Chrome",
    "statusFg",
    "Status line text",
    "$fg2",
    { contrast: { against: "ui.statusBg", level: "text" } },
  ],
  ["Chrome", "pmenuBg", "Popup menu", "$bg2", bg],
  ["Chrome", "pmenuSel", "Popup selection", "$bg3", bg],
  ["Chrome", "tabInactiveBg", "Inactive tab", "$bg0", bg],
  ["Diagnostics", "error", "Error", "$red", onEditor()],
  ["Diagnostics", "warning", "Warning", "$yellow", onEditor()],
  ["Diagnostics", "info", "Info", "$blue", onEditor()],
  ["Diagnostics", "hint", "Hint", "$cyan", onEditor()],
  ["Diagnostics", "ok", "OK", "$green", onEditor()],
  [
    "Diff",
    "diffAdd",
    "Added line",
    { dark: mix(BG, "$green", 0.2), light: mix(BG, "$green", 0.22) },
    bg,
  ],
  [
    "Diff",
    "diffChange",
    "Changed line",
    { dark: mix(BG, "$blue", 0.15), light: mix(BG, "$blue", 0.18) },
    bg,
  ],
  [
    "Diff",
    "diffDelete",
    "Deleted line",
    { dark: mix(BG, "$red", 0.2), light: mix(BG, "$red", 0.22) },
    bg,
  ],
  [
    "Diff",
    "diffText",
    "Changed text",
    { dark: mix(BG, "$blue", 0.35), light: mix(BG, "$blue", 0.35) },
    bg,
  ],
  ["Diff", "gitAdd", "Git sign: added", "$green", onEditor("secondary")],
  ["Diff", "gitChange", "Git sign: changed", "$blue", onEditor("secondary")],
  ["Diff", "gitDelete", "Git sign: deleted", "$red", onEditor("secondary")],
]);

// ---------------------------------------------------------------- herdr

/** Token names accepted by herdr's `[theme.custom]` table (checked against herdr 0.9.0). */
const HERDR = defineGroup("herdr", [
  ["Surfaces", "panel_bg", "panel_bg", BG, { ...bg, hint: "Pane background" }],
  ["Surfaces", "sidebar_bg", "sidebar_bg", "@ui.panelBg", { ...bg, hint: "Sidebar background" }],
  [
    "Surfaces",
    "active_row_bg",
    "active_row_bg",
    "$bg2",
    { ...bg, hint: "Active space / agent row" },
  ],
  ["Surfaces", "selection_bg", "selection_bg", "$bg3", { ...bg, hint: "Navigate-mode cursor row" }],
  ["Surfaces", "surface_dim", "surface_dim", "$bg0", bg],
  ["Surfaces", "surface0", "surface0", "$bg2", bg],
  ["Surfaces", "surface1", "surface1", "$bg3", bg],
  ["Surfaces", "overlay0", "overlay0", "$bg4", onBg("decorative")],
  ["Surfaces", "overlay1", "overlay1", "$fg3", onBg("secondary")],
  ["Text", "text", "text", "$fg1", { contrast: { against: "herdr.sidebar_bg", level: "text" } }],
  [
    "Text",
    "subtext0",
    "subtext0",
    "$fg2",
    { contrast: { against: "herdr.sidebar_bg", level: "secondary" } },
  ],
  [
    "Accents",
    "accent",
    "accent",
    "@ui.accent",
    { ...onBg(), hint: "Highlights, borders, navigation" },
  ],
  ["Accents", "red", "red", "$red", onBg()],
  ["Accents", "peach", "peach", "$orange", onBg()],
  ["Accents", "yellow", "yellow", "$yellow", onBg()],
  ["Accents", "green", "green", "$green", onBg()],
  ["Accents", "teal", "teal", "$cyan", onBg()],
  ["Accents", "blue", "blue", "$blue", onBg()],
  ["Accents", "mauve", "mauve", "$purple", onBg()],
]);

// ---------------------------------------------------------------- Claude Code

const lighter = (e: Expr) => shade(e, 0.08);

/** The 72 keys of Claude Code's theme (read from Claude Code 2.1.314). */
const CLAUDE = defineGroup("claude", [
  ["Brand", "claude", "claude", "$orange", onBg()],
  ["Brand", "claudeShimmer", "claudeShimmer", lighter("@claude.claude"), onBg()],
  ["Brand", "clawd_body", "clawd_body", "@claude.claude", onBg()],
  ["Brand", "clawd_background", "clawd_background", BG, bg],
  ["Brand", "briefLabelClaude", "briefLabelClaude", "@claude.claude", onBg()],
  ["Brand", "briefLabelYou", "briefLabelYou", "$blue", onBg()],
  ["Brand", "claudeBlue_FOR_SYSTEM_SPINNER", "claudeBlue_FOR_SYSTEM_SPINNER", "$blue", onBg()],
  [
    "Brand",
    "claudeBlueShimmer_FOR_SYSTEM_SPINNER",
    "claudeBlueShimmer_FOR_SYSTEM_SPINNER",
    lighter("@claude.claudeBlue_FOR_SYSTEM_SPINNER"),
    onBg(),
  ],
  ["Text", "text", "text", FG, onBg()],
  ["Text", "inverseText", "inverseText", BG, bg],
  ["Text", "inactive", "inactive", "$fg3", onBg("secondary")],
  ["Text", "inactiveShimmer", "inactiveShimmer", "$fg2", onBg("secondary")],
  ["Text", "subtle", "subtle", "$bg4", onBg("decorative")],
  ["Text", "suggestion", "suggestion", "$blue", onBg()],
  ["Text", "remember", "remember", "$blue", onBg()],
  ["Text", "promptBorder", "promptBorder", "$fg3", onBg("decorative")],
  ["Text", "promptBorderShimmer", "promptBorderShimmer", "$fg2", onBg("decorative")],
  ["Modes", "permission", "permission", "$blue", onBg()],
  ["Modes", "permissionShimmer", "permissionShimmer", lighter("@claude.permission"), onBg()],
  ["Modes", "planMode", "planMode", "$cyan", onBg()],
  ["Modes", "autoAccept", "autoAccept", "$purple", onBg()],
  ["Modes", "autoAcceptShimmer", "autoAcceptShimmer", lighter("@claude.autoAccept"), onBg()],
  ["Modes", "skill", "skill", "$purple", onBg()],
  ["Modes", "bashBorder", "bashBorder", "$pink", onBg()],
  ["Modes", "ide", "ide", "$blue", onBg()],
  [
    "Modes",
    "background",
    "background",
    "$cyan",
    { ...onBg(), hint: "Background-task accent (a foreground color)" },
  ],
  ["Modes", "merged", "merged", "$purple", onBg()],
  ["Modes", "fastMode", "fastMode", "$orange", onBg()],
  ["Modes", "fastModeShimmer", "fastModeShimmer", lighter("@claude.fastMode"), onBg()],
  ["Modes", "effortUltra", "effortUltra", "$purple", onBg()],
  ["Modes", "professionalBlue", "professionalBlue", "$blue", onBg()],
  ["Modes", "chromeYellow", "chromeYellow", "$yellow", onBg()],
  ["Modes", "rate_limit_fill", "rate_limit_fill", "$blue", onBg()],
  ["Modes", "rate_limit_empty", "rate_limit_empty", "$bg3", bg],
  ["Status", "success", "success", "$green", onBg()],
  ["Status", "error", "error", "$red", onBg()],
  ["Status", "warning", "warning", "$yellow", onBg()],
  ["Status", "warningShimmer", "warningShimmer", lighter("@claude.warning"), onBg()],
  ["Diff", "diffAdded", "diffAdded", mix(BG, "$green", 0.3), bg],
  ["Diff", "diffRemoved", "diffRemoved", mix(BG, "$red", 0.3), bg],
  ["Diff", "diffAddedDimmed", "diffAddedDimmed", mix(BG, "$green", 0.15), bg],
  ["Diff", "diffRemovedDimmed", "diffRemovedDimmed", mix(BG, "$red", 0.15), bg],
  ["Diff", "diffAddedWord", "diffAddedWord", mix(BG, "$green", 0.6), bg],
  ["Diff", "diffRemovedWord", "diffRemovedWord", mix(BG, "$red", 0.6), bg],
  ["Surfaces", "userMessageBackground", "userMessageBackground", "$bg2", bg],
  ["Surfaces", "userMessageBackgroundHover", "userMessageBackgroundHover", "$bg3", bg],
  ["Surfaces", "composerSidebarBackground", "composerSidebarBackground", "$bg0", bg],
  ["Surfaces", "selectionBg", "selectionBg", "@ui.selection", bg],
  [
    "Surfaces",
    "bashMessageBackgroundColor",
    "bashMessageBackgroundColor",
    mix(BG, "$pink", 0.08),
    bg,
  ],
  ["Surfaces", "memoryBackgroundColor", "memoryBackgroundColor", mix(BG, "$cyan", 0.08), bg],
  ["Subagents", "red_FOR_SUBAGENTS_ONLY", "red_FOR_SUBAGENTS_ONLY", "$red", onBg()],
  ["Subagents", "orange_FOR_SUBAGENTS_ONLY", "orange_FOR_SUBAGENTS_ONLY", "$orange", onBg()],
  ["Subagents", "yellow_FOR_SUBAGENTS_ONLY", "yellow_FOR_SUBAGENTS_ONLY", "$yellow", onBg()],
  ["Subagents", "green_FOR_SUBAGENTS_ONLY", "green_FOR_SUBAGENTS_ONLY", "$green", onBg()],
  ["Subagents", "cyan_FOR_SUBAGENTS_ONLY", "cyan_FOR_SUBAGENTS_ONLY", "$cyan", onBg()],
  ["Subagents", "blue_FOR_SUBAGENTS_ONLY", "blue_FOR_SUBAGENTS_ONLY", "$blue", onBg()],
  ["Subagents", "purple_FOR_SUBAGENTS_ONLY", "purple_FOR_SUBAGENTS_ONLY", "$purple", onBg()],
  ["Subagents", "pink_FOR_SUBAGENTS_ONLY", "pink_FOR_SUBAGENTS_ONLY", "$pink", onBg()],
  ...(
    [
      ["red", "$red"],
      ["orange", "$orange"],
      ["yellow", "$yellow"],
      ["green", "$green"],
      ["blue", "$blue"],
      ["indigo", "$purple"],
      ["violet", "$pink"],
    ] as const
  ).flatMap(
    ([name, ref]) =>
      [
        ["Rainbow", `rainbow_${name}`, `rainbow_${name}`, ref, onBg()],
        [
          "Rainbow",
          `rainbow_${name}_shimmer`,
          `rainbow_${name}_shimmer`,
          shade(`@claude.rainbow_${name}`, 0.12),
          onBg(),
        ],
      ] as [string, string, string, TokenDef["default"], Opts][],
  ),
]);

// ---------------------------------------------------------------- WeeChat

/** Nick colors, joined into `weechat.color.chat_nick_colors` on export. */
export const WEECHAT_NICK_COUNT = 12;
const NICK_DEFAULTS: Ref[] = [6, 5, 2, 3, 4, 1, 14, 13, 10, 12, 11, 9].map(
  (i) => `@terminal.ansi${i}` as Ref,
);

/** WeeChat draws only terminal colors exactly, so its defaults point at ANSI slots. */
const ansi = (i: number): Ref => `@terminal.ansi${i}` as Ref;

/**
 * Keys are WeeChat option names with dots replaced by `:` so the token id stays
 * `weechat.<key>`. `buflist:*` keys are written into buflist format strings.
 */
const WEECHAT = defineGroup("weechat", [
  ["Chat", "weechat.color:chat", "chat", FG, onBg()],
  ["Chat", "weechat.color:chat_bg", "chat_bg", BG, bg],
  ["Chat", "weechat.color:chat_time", "chat_time", ansi(8), onBg("secondary")],
  [
    "Chat",
    "weechat.color:chat_time_delimiters",
    "chat_time_delimiters",
    ansi(8),
    onBg("decorative"),
  ],
  ["Chat", "weechat.color:chat_delimiters", "chat_delimiters", ansi(8), onBg("decorative")],
  ["Chat", "weechat.color:chat_prefix_suffix", "chat_prefix_suffix", ansi(8), onBg("decorative")],
  ["Chat", "weechat.color:chat_prefix_join", "chat_prefix_join", ansi(10), onBg()],
  ["Chat", "weechat.color:chat_prefix_quit", "chat_prefix_quit", ansi(9), onBg()],
  ["Chat", "weechat.color:chat_prefix_network", "chat_prefix_network", ansi(13), onBg()],
  ["Chat", "weechat.color:chat_prefix_error", "chat_prefix_error", ansi(11), onBg()],
  ["Chat", "weechat.color:chat_prefix_action", "chat_prefix_action", ansi(15), onBg()],
  ["Chat", "weechat.color:chat_prefix_more", "chat_prefix_more", ansi(13), onBg()],
  ["Chat", "weechat.color:chat_prefix_buffer", "chat_prefix_buffer", ansi(7), onBg("secondary")],
  ["Chat", "weechat.color:chat_nick_self", "chat_nick_self", ansi(15), onBg()],
  ["Chat", "weechat.color:chat_nick_offline", "chat_nick_offline", ansi(8), onBg("secondary")],
  ["Chat", "weechat.color:chat_host", "chat_host", ansi(14), onBg()],
  ["Chat", "weechat.color:chat_channel", "chat_channel", ansi(15), onBg()],
  ["Chat", "weechat.color:chat_server", "chat_server", ansi(3), onBg()],
  ["Chat", "weechat.color:chat_buffer", "chat_buffer", ansi(15), onBg()],
  ["Chat", "weechat.color:chat_highlight", "chat_highlight", ansi(11), onBg()],
  ["Chat", "weechat.color:chat_highlight_bg", "chat_highlight_bg", BG, bg],
  ["Chat", "weechat.color:chat_read_marker", "chat_read_marker", ansi(13), onBg("decorative")],
  ["Chat", "weechat.color:chat_text_found", "chat_text_found", ansi(3), onBg()],
  ["Chat", "weechat.color:chat_tags", "chat_tags", ansi(9), onBg()],
  [
    "Chat",
    "weechat.color:chat_inactive_window",
    "chat_inactive_window",
    ansi(8),
    onBg("secondary"),
  ],
  [
    "Chat",
    "weechat.color:chat_inactive_buffer",
    "chat_inactive_buffer",
    ansi(8),
    onBg("secondary"),
  ],
  ...Array.from(
    { length: WEECHAT_NICK_COUNT },
    (_, i) =>
      [
        "Nick colors",
        `nick_color_${i + 1}`,
        `nick color ${i + 1}`,
        NICK_DEFAULTS[i] ?? ansi(15),
        onBg(),
      ] as [string, string, string, TokenDef["default"], Opts],
  ),
  ["Bars", "weechat.bar:status.color_bg", "status bar bg", ansi(0), bg],
  [
    "Bars",
    "weechat.bar:status.color_fg",
    "status bar fg",
    ansi(7),
    { contrast: { against: "weechat.weechat.bar:status.color_bg", level: "text" } },
  ],
  ["Bars", "weechat.bar:status.color_delim", "status bar delimiters", ansi(8), {}],
  ["Bars", "weechat.bar:title.color_bg", "title bar bg", ansi(0), bg],
  [
    "Bars",
    "weechat.bar:title.color_fg",
    "title bar fg",
    ansi(15),
    { contrast: { against: "weechat.weechat.bar:title.color_bg", level: "text" } },
  ],
  ["Bars", "weechat.bar:input.color_bg", "input bar bg", BG, bg],
  ["Bars", "weechat.bar:input.color_fg", "input bar fg", FG, onBg()],
  ["Bars", "weechat.bar:buflist.color_bg", "buflist bg", BG, bg],
  ["Bars", "weechat.bar:nicklist.color_bg", "nicklist bg", BG, bg],
  ["Bars", "weechat.color:separator", "separator", ansi(8), onBg("decorative")],
  ["Status", "weechat.color:status_name", "status_name", ansi(15), {}],
  ["Status", "weechat.color:status_number", "status_number", ansi(11), {}],
  ["Status", "weechat.color:status_time", "status_time", ansi(7), {}],
  ["Status", "weechat.color:status_more", "status_more", ansi(11), {}],
  ["Status", "weechat.color:status_data_msg", "status_data_msg", ansi(11), {}],
  ["Status", "weechat.color:status_data_private", "status_data_private", ansi(10), {}],
  ["Status", "weechat.color:status_data_highlight", "status_data_highlight", ansi(13), {}],
  ["Status", "weechat.color:status_data_other", "status_data_other", ansi(7), {}],
  ["Status", "weechat.color:status_filter", "status_filter", ansi(10), {}],
  ["Buflist", "buflist:current_bg", "current buffer bg", ansi(0), bg],
  ["Buflist", "buflist:hotlist_low", "hotlist low", ansi(7), onBg("secondary")],
  ["Buflist", "buflist:hotlist_message", "hotlist message", ansi(11), onBg()],
  ["Buflist", "buflist:hotlist_private", "hotlist private", ansi(10), onBg()],
  ["Buflist", "buflist:hotlist_highlight", "hotlist highlight", ansi(13), onBg()],
  ["Misc", "weechat.color:input_actions", "input_actions", ansi(10), onBg()],
  ["Misc", "weechat.color:input_text_not_found", "input_text_not_found", ansi(9), onBg()],
  ["Misc", "weechat.color:nicklist_away", "nicklist_away", ansi(8), onBg("secondary")],
  ["Misc", "weechat.color:nicklist_group", "nicklist_group", ansi(10), onBg()],
  ["Misc", "weechat.color:emphasized", "emphasized", ansi(11), onBg()],
  ["IRC", "irc.color:input_nick", "irc input_nick", ansi(14), onBg()],
  ["IRC", "irc.color:message_join", "irc message_join", ansi(10), onBg()],
  ["IRC", "irc.color:message_quit", "irc message_quit", ansi(9), onBg()],
  ["IRC", "irc.color:message_kick", "irc message_kick", ansi(9), onBg()],
  ["IRC", "irc.color:message_chghost", "irc message_chghost", ansi(3), onBg()],
  ["IRC", "irc.color:notice", "irc notice", ansi(10), onBg()],
  ["IRC", "irc.color:reason_quit", "irc reason_quit", ansi(8), onBg("secondary")],
  ["IRC", "irc.color:topic_new", "irc topic_new", ansi(15), onBg()],
  ["IRC", "irc.color:topic_old", "irc topic_old", ansi(8), onBg("secondary")],
]);

export const TOKENS: TokenDef[] = [...TERMINAL, ...SYNTAX, ...UI, ...HERDR, ...CLAUDE, ...WEECHAT];
export const TOKEN_MAP: ReadonlyMap<TokenId, TokenDef> = new Map(TOKENS.map((t) => [t.id, t]));

export function tokensOf(group: GroupId): TokenDef[] {
  return TOKENS.filter((t) => t.group === group);
}

export function isTokenId(value: string): value is TokenId {
  return TOKEN_MAP.has(value as TokenId);
}

export function defaultExpr(def: TokenDef, variant: Variant): Expr {
  const d = def.default;
  return typeof d === "object" && d !== null && "dark" in d ? d[variant] : d;
}

/** WeeChat option name for a weechat token key (`weechat.color:chat` → `weechat.color.chat`). */
export function weechatOption(key: string): string {
  return key.replace(":", ".");
}
